import { FC, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';
import { QcsButton } from '../../common/basic/QcsButton';
import { DashboardTileDto } from '@api/models/dashboard/DashboardTileDto';
import { useAppSelector } from '../../../store';
import { selectAnalyticsEntry } from '../../../store/entities/analytics';
import { FetchState } from '../../../store/fetchState';
import { EMPTY_CELL, formatCellValue } from '../analyticsColumns';
import { DOMAIN_CONFIG_MAP } from '../analyticsDomainRegistry';
import { isTotalsMetricKey } from '../analyticsTotalsConfig';
import { MiniChartTile } from '../AnalyticsStyles';
import { EmptyState } from './AnalyticsSharedStyles';
import { KpiLabel, KpiLabelRow, KpiValue } from './KpiTileStyles';

interface Props {
  tile: DashboardTileDto;
  reduxKey: string;
  onClick: () => void;
  onRetry: () => void;
}

export const SortableKpiTile: FC<Props> = ({ tile, reduxKey, onClick, onRetry }) => {
  const { t, i18n } = useTranslation();
  const entry = useAppSelector(selectAnalyticsEntry(reduxKey));
  const loading = entry.state === FetchState.Loading;
  const hasError = entry.state === FetchState.Error;
  const totals = entry.data?.totals ?? null;

  const metricKey = isTotalsMetricKey(tile.metric) ? tile.metric : undefined;
  const metricConfig = metricKey ? DOMAIN_CONFIG_MAP[tile.domain]?.totalsMetrics[metricKey] : undefined;

  const label = tile.title?.trim() || (metricConfig ? t(metricConfig.labelKey) : '');
  const value =
    totals && metricKey && metricConfig
      ? formatCellValue(totals[metricKey], metricConfig.columnType, i18n)
      : EMPTY_CELL;

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      {...(hasError
        ? {}
        : { role: 'button', tabIndex: 0, onClick, onKeyDown: handleKeyDown, 'aria-label': `${label}: ${value}`, 'aria-busy': loading })}
    >
      <MiniChartTile compact sx={{ justifyContent: 'space-between' }}>
        {hasError ? (
          <EmptyState style={{ flex: 1 }}>
            <span>{t('analytics.dashboard.noData')}</span>
            <QcsButton variant="outlined" stopPropagation onClick={onRetry}>
              {t('analytics.dashboard.retry')}
            </QcsButton>
          </EmptyState>
        ) : (
          <>
            <KpiLabelRow>
              <KpiLabel>{label}</KpiLabel>
            </KpiLabelRow>
            {loading ? (
              <Skeleton variant="text" width="60%" height={32} />
            ) : (
              <KpiValue tone="neutral">{value}</KpiValue>
            )}
          </>
        )}
      </MiniChartTile>
    </div>
  );
};
